import React, { useMemo, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
    useDraggable,
    useDroppable,
    closestCorners,
    DragStartEvent,
    DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../../lib/cn';
import { Lead, LeadColor, LeadStage, LEAD_STAGES } from '../../types/leads.types';
import { LeadCard } from './LeadCard';

interface LeadKanbanProps {
    leads: Lead[];
    dueReminderLeadIds?: Set<string>;
    onCardClick: (lead: Lead) => void;
    onStageChange: (lead: Lead, newStage: LeadStage) => void;
    onQuickLogged?: () => void;
    onColorChange?: (lead: Lead, color: LeadColor | null) => void;
}

function DraggableCard({
    lead,
    hasDueReminder,
    onClick,
    onQuickLogged,
    onColorChange,
}: {
    lead: Lead;
    hasDueReminder: boolean;
    onClick: () => void;
    onQuickLogged?: () => void;
    onColorChange?: (lead: Lead, color: LeadColor | null) => void;
}) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: lead.id,
        data: { stage: lead.stage },
    });

    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.4 : 1,
    };

    return (
        <div ref={setNodeRef} style={{ ...style, touchAction: 'pan-y' }} {...listeners} {...attributes}>
            <LeadCard
                lead={lead}
                compact
                hasDueReminder={hasDueReminder}
                onClick={onClick}
                onQuickLogged={onQuickLogged}
                onColorChange={onColorChange ? (color) => onColorChange(lead, color) : undefined}
            />
        </div>
    );
}

function Column({
    stage,
    label,
    headerClass,
    leads,
    dueReminderLeadIds,
    onCardClick,
    onQuickLogged,
    onColorChange,
}: {
    stage: LeadStage;
    label: string;
    headerClass: string;
    leads: Lead[];
    dueReminderLeadIds: Set<string>;
    onCardClick: (lead: Lead) => void;
    onQuickLogged?: () => void;
    onColorChange?: (lead: Lead, color: LeadColor | null) => void;
}) {
    const { setNodeRef, isOver } = useDroppable({ id: stage });

    return (
        <div className="flex flex-col w-[80vw] max-w-[18rem] sm:w-72 flex-shrink-0 snap-start">
            <div className={cn('flex items-center justify-between px-3 py-2 rounded-t-lg border font-semibold text-sm', headerClass)}>
                <span>{label}</span>
                <span className="text-xs bg-white/60 rounded-full px-2 py-0.5">{leads.length}</span>
            </div>
            <div
                ref={setNodeRef}
                className={cn(
                    'flex-1 min-h-[120px] space-y-2 p-2 rounded-b-lg border border-t-0 bg-gray-50/80 transition-colors',
                    isOver && 'bg-blue-50 ring-2 ring-inset ring-blue-300'
                )}
            >
                {leads.map((lead) => (
                    <DraggableCard
                        key={lead.id}
                        lead={lead}
                        hasDueReminder={dueReminderLeadIds.has(lead.id)}
                        onClick={() => onCardClick(lead)}
                        onQuickLogged={onQuickLogged}
                        onColorChange={onColorChange}
                    />
                ))}
                {leads.length === 0 && (
                    <div className="text-center text-xs text-gray-400 py-6 border border-dashed rounded-lg">
                        Nessun lead
                    </div>
                )}
            </div>
        </div>
    );
}

export function LeadKanban({ leads, dueReminderLeadIds, onCardClick, onStageChange, onQuickLogged, onColorChange }: LeadKanbanProps) {
    const [activeLead, setActiveLead] = useState<Lead | null>(null);
    const dueSet = dueReminderLeadIds ?? new Set<string>();

    // Mouse drags start after a small move. Touch drags need a press-and-hold,
    // otherwise dnd-kit swallows the gesture and the board cannot be scrolled
    // with a finger.
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
    );

    const byStage = useMemo(() => {
        const map: Record<LeadStage, Lead[]> = {
            nuovo: [], contattato: [], qualificato: [], proposta: [], vinto: [], perso: [],
        };
        for (const lead of leads) {
            (map[lead.stage] ?? map.nuovo).push(lead);
        }
        return map;
    }, [leads]);

    const handleDragStart = (e: DragStartEvent) => {
        const lead = leads.find((l) => l.id === e.active.id);
        setActiveLead(lead ?? null);
    };

    const handleDragEnd = (e: DragEndEvent) => {
        setActiveLead(null);
        const { active, over } = e;
        if (!over) return;

        const lead = leads.find((l) => l.id === active.id);
        if (!lead) return;

        const newStage = over.id as LeadStage;
        if (!LEAD_STAGES.some((s) => s.key === newStage)) return;
        if (newStage === lead.stage) return;

        onStageChange(lead, newStage);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="flex gap-3 overflow-x-auto overscroll-x-contain snap-x snap-mandatory sm:snap-none pb-4 -mx-3 px-3 sm:mx-0 sm:px-0">
                {LEAD_STAGES.map((s) => (
                    <Column
                        key={s.key}
                        stage={s.key}
                        label={s.label}
                        headerClass={s.headerClass}
                        leads={byStage[s.key]}
                        dueReminderLeadIds={dueSet}
                        onCardClick={onCardClick}
                        onQuickLogged={onQuickLogged}
                        onColorChange={onColorChange}
                    />
                ))}
            </div>

            <DragOverlay>
                {activeLead ? (
                    <div className="w-[80vw] max-w-[18rem] sm:w-72">
                        <LeadCard lead={activeLead} compact hasDueReminder={dueSet.has(activeLead.id)} />
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}

export default LeadKanban;
